import { createRootRouteWithContext, Outlet, useRouterState } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { NavigationProgress, Sidebar, TopBar } from '@/components/chrome';
import { MaintenanceBanner } from '@/components/maintenance-banner';
import { UpdateBanner } from '@/components/update-banner';
import { swapToConfirmedRelease } from '@/lib/app-release';
import { useMe } from '@/auth/useMe';
import { isPublicPath } from '@/lib/public-paths';

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  // Route-data exemption: the layout shell renders no server data of its own; the session it
  // needs is fetched by `requireSession` in `beforeLoad`, which runs before any child
  // loader.
  component: RootComponent,
  // A page change with a newer release confirmed lands on it as a full page load, before any
  // child route loads (lib/app-release.ts). Search-only changes, hover preloads, the first load,
  // held work and leaving Admin Config stay in-app.
  beforeLoad: ({ location }) => swapToConfirmedRelease(location),
  // A `beforeLoad` arms the router's 150 ms pending timer for its match, and the root's would
  // put the skeleton over every route that opted out of it (a filter change under a slow
  // session guard). The root never shows one (Astra r3, 2026-10-21).
  pendingMs: Infinity,
});

function RootComponent() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const publicPath = isPublicPath(pathname);
  // Not merely wasted on a public path — the 401 it provokes fires the global
  // unauthorized handler, and chrome is hidden there regardless of the answer.
  const { data: me } = useMe({ enabled: !publicPath });
  // Chrome stays hidden on public paths even when a session exists (e.g. an
  // admin opening an invite link they minted for someone else).
  const showChrome = !!me && !publicPath;
  if (!showChrome) {
    return (
      <>
        <MaintenanceBanner />
        <UpdateBanner />
        <Outlet />
      </>
    );
  }
  // The progress bar lives here, not in the topbar: it is fixed to the
  // viewport, and the phone layout hides the topbar (responsive.css ≤767). Both
  // it and its live region are out of flow, so the `.app` grid gains no track.
  return (
    <div className="app">
      <NavigationProgress />
      <TopBar />
      <Sidebar />
      <main className="main">
        <MaintenanceBanner />
        <UpdateBanner />
        <Outlet />
      </main>
    </div>
  );
}
